import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { setPageTitle } from "../../../Store/modules/mms/actions";
import MmsApiService from "Services/MMS/mms-api.service";
import CreateEditMetricModal from "Components/modals/AddEditMetricModal/create-edit-metric-modal";
import DeleteItemModal from "Components/modals/delete-item-modal/delete-item-modal.component";
import {
  IconButton,
  MenuItem,
  Popover,
  CircularProgress,
  Fade,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AlertToastr from "utils/alert";

const Metrics = (props) => {
  const { setPageTitle } = props;
  const history = useHistory();
  const [metrics, setMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState(null);
  
  const getOperatorSymbol = (operator) => {
    switch (operator) {
      case 'smallerThan':
        return '<';
      case 'biggerThan':
        return '>';
      case 'equals':
        return '=';
      default:
        return operator; 
    }
  };

  const renderConditionalChannels = (conditionalChannels) => {
    if (!conditionalChannels || conditionalChannels.length === 0) {
      return <span className="textGray">-</span>;
    }

    return (
      <div>
        {conditionalChannels.map((channel, index) => (
          <div key={channel.id} style={{ marginBottom: '4px' }}>
            <span style={{ fontWeight: 'bold' }}>{channel.name}</span>
            <span style={{ margin: '0 4px' }}>
              {getOperatorSymbol(channel.operator)} {channel.value} {channel.unit}
            </span>
          </div>
        ))}
      </div>
    );
  };


  const fetchMetrics = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await MmsApiService.getMetrics();

      const metricsData = response.data?.items || response.data || [];
      setMetrics(metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageTitle("Metrics");
    fetchMetrics();
  }, [setPageTitle, fetchMetrics]);

  useEffect(() => {
    const handleRefreshMetrics = () => {
      setTimeout(() => {
        fetchMetrics();
      }, 1000);
    };

    window.addEventListener("refreshMetrics", handleRefreshMetrics);

    return () => {
      window.removeEventListener("refreshMetrics", handleRefreshMetrics);
    };
  }, [fetchMetrics]);

  const handleMenuClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMetric(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMetric(null);
  };

  const handleEdit = async () => {
    try {
      if (selectedMetric?.id) {
        const response = await MmsApiService.getMetric(selectedMetric.id);
        setEditingMetric(response.data);
        setIsEditModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching metric for editing:", error);
      AlertToastr.showErrorAlert("Error loading metric data for editing");
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedMetric) {
      setMetricToDelete(selectedMetric);
      setIsDeleteModalOpen(true);
    }
    handleMenuClose();
  };

  const handleMetricClick = (metricId) => {
    history.push(`/metrics/${metricId}`);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditingMetric(null);
  };

  const handleEditSuccess = () => {
    handleEditModalClose();
    setTimeout(() => {
      fetchMetrics();
    }, 1000);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setMetricToDelete(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (metricToDelete?.id) {
        await MmsApiService.deleteMetric(metricToDelete.id);

        setTimeout(() => {
          fetchMetrics();
        }, 1000);
        AlertToastr.showAlert("Metric deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting metric:", error);
      AlertToastr.showErrorAlert("Error deleting metric. Please try again.");
    } finally {
      handleDeleteModalClose();
    }
  };

  return (
    <>
      <div className="pmPageHolder">
        <div className="pmPageBlock">
          <div className="pmPageBlockTitle">Metrics</div>
          <div className="pmTableHolder" style={{ marginTop: "50px" }}>
            <table className="pmTable assetTypesTable">
              <thead>
                <tr>
                  <th>Metric Name</th>
                  <th>Main Channel</th>
                  <th>Conditional Channels</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((item, index) => (
                  <tr key={item.id}>
                    <td
                      className="text-semi-bold"
                      style={{
                        verticalAlign: "top",
                      }}
                    >
                      {item.name}
                    </td>
                    <td
                      style={{
                        verticalAlign: "top",
                        borderLeft: "0.5px solid #e0e0e0",
                        paddingLeft: "10px",
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: "bold" }}>{item.channelName}</span>
                        <span style={{ margin: "0 4px" }}>
                          {getOperatorSymbol(item.operator)} {item.value} {item.unit}
                        </span>
                      </div>
                    </td>
                    <td
                      style={{
                        verticalAlign: "top",
                        borderLeft: "0.5px solid #e0e0e0",
                        paddingLeft: "10px",
                      }}
                    >
                      {renderConditionalChannels(item.conditionalChannels)}
                    </td>
                    <td className="btnCell">
                      <IconButton
                        aria-label="more"
                        size="small"
                        className="dropDownBtn"
                        onClick={(e) => handleMenuClick(e, item)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {isLoading && (
              <div className="loaderHolder">
                <Fade in={isLoading} unmountOnExit>
                  <CircularProgress />
                </Fade>
              </div>
            )}

            {!isLoading && metrics.length === 0 && (
              <div className="emptyInfoHolder">
                <p className="textGray text-center">No Metrics found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Popover
        anchorEl={menuAnchorEl}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        disableScrollLock={true}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        className="pmPopover equipmentSubMenu"
      >
        <MenuItem key="edit" onClick={handleEdit} className="menuItem edit">
          Edit
        </MenuItem>
        <MenuItem
          key="delete"
          onClick={handleDelete}
          className="menuItem delete"
        >
          Delete
        </MenuItem>
      </Popover>

      <CreateEditMetricModal
        isOpen={isEditModalOpen}
        isOpenChangeHandler={handleEditModalClose}
        editingData={editingMetric}
        onSuccess={handleEditSuccess}
      />

      <DeleteItemModal
        isOpen={isDeleteModalOpen}
        isOpenManager={setIsDeleteModalOpen}
        message={`Are you sure you want to delete Metric "${metricToDelete?.name}"?`}
        onDeleteModalClose={handleConfirmDelete}
      />
    </>
  );
};

const mapStateToProps = ({ mms }) => ({
  currentDepartment: mms.currentDepartment,
});

const mapDispatchToProps = {
  setPageTitle,
};

export default connect(mapStateToProps, mapDispatchToProps)(Metrics);
